<vs-card
  *ngIf="claim$ | async"
  theme="default"
  class="flex flex-col"
  [class.!border-0]="embedded"
  [class.!shadow-none]="embedded"
  [class.!bg-transparent]="embedded"
>
  <ng-container *vsLet="state$ | async as state">
    <vs-card-header
      *ngIf="!embedded"
      [config]="{
        title: 'Audit Trail',
        subtitle: 'A permanent record of every decision, amount and record change on this claim.'
      }"
    >
      <vs-badge slot="after-title" theme="default">
        {{ state?.totalItems || 0 }} {{ state?.totalItems === 1 ? 'Entry' : 'Entries' }}
      </vs-badge>

      <button
        vs-icon-button
        slot="end"
        appearance="outline"
        tooltipStyle="light"
        vsTooltip="Refresh"
        [disabled]="state?.isLoading"
        (click)="refresh()"
      >
        <vs-icon name="refresh-cw-01" [class.animate-spin]="state?.isLoading"></vs-icon>
      </button>
    </vs-card-header>

    <vs-card-content class="flex flex-col gap-4" [class.!p-0]="embedded">
      <!-- Filters: one kind of record, filtered on the server -->
      <div *vsLet="entityType$ | async as entityType" class="flex gap-2 overflow-x-auto pb-1 -mb-1">
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium whitespace-nowrap transition-colors hover:bg-gray-50"
          [class.bg-primary-50]="!entityType"
          [class.border-primary-300]="!entityType"
          [class.text-primary-700]="!entityType"
          [class.text-gray-600]="entityType"
          (click)="setEntityType(null)"
        >
          All
        </button>

        <button
          *ngFor="let category of categories"
          type="button"
          class="flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-medium whitespace-nowrap transition-colors hover:bg-gray-50"
          [class.bg-primary-50]="entityType === category.entityType"
          [class.border-primary-300]="entityType === category.entityType"
          [class.text-primary-700]="entityType === category.entityType"
          [class.text-gray-600]="entityType !== category.entityType"
          (click)="setEntityType(category.entityType)"
        >
          <vs-icon size="14" [name]="$any(category.icon)"></vs-icon>
          {{ category.label }}
        </button>
      </div>

      <!-- First load; later loads keep the list and dim it -->
      <div *ngIf="state?.isLoading && !state?.days?.length" class="flex flex-col gap-4">
        <div *ngFor="let row of [1, 2, 3]" class="flex gap-3 animate-pulse">
          <div class="w-9 h-9 rounded-full bg-gray-100 shrink-0"></div>
          <div class="flex-1 rounded-lg border p-4 flex flex-col gap-2">
            <div class="h-3 w-2/3 rounded bg-gray-100"></div>
            <div class="h-3 w-1/3 rounded bg-gray-100"></div>
            <div class="h-8 w-full rounded bg-gray-50"></div>
          </div>
        </div>
      </div>

      <!-- Failed to load -->
      <div
        *ngIf="state?.hasError"
        class="flex flex-col items-center justify-center gap-2 py-12 text-center rounded-lg border border-dashed"
      >
        <vs-icon name="alert-triangle" size="28" class="text-danger-500"></vs-icon>
        <p class="text-sm font-semibold text-gray-700">The audit trail couldn't be loaded.</p>
        <button vs-button appearance="outline" size="sm" (click)="refresh()">Try again</button>
      </div>

      <!-- Nothing recorded -->
      <ng-container *ngIf="state && !state.isLoading && !state.hasError && !state.days.length">
        <vs-icon-empty-state
          name="file-search-02"
          [config]="{
            title: (entityType$ | async) ? 'No changes of this kind yet.' : 'No changes have been recorded yet.',
            subtitle: 'Decisions, amounts and record changes on this claim will appear here.'
          }"
        ></vs-icon-empty-state>
      </ng-container>

      <!-- Entries, grouped by day -->
      <div
        *ngIf="state && !state.hasError && state.days.length"
        class="flex flex-col transition-opacity"
        [class.opacity-50]="state.isLoading"
        [class.pointer-events-none]="state.isLoading"
      >
        <div *ngFor="let day of state.days; trackBy: trackByLabel" class="flex flex-col">
          <div class="flex items-center gap-3 mb-3">
            <p class="text-xs font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">{{ day.label }}</p>
            <div class="h-px flex-1 bg-gray-200"></div>
          </div>

          <div *ngFor="let entry of day.entries; let last = last; trackBy: trackById" class="flex gap-3">
            <!-- Who, and what kind of record -->
            <div class="flex flex-col items-center shrink-0">
              <div class="relative">
                <ng-container *ngIf="!entry.isSystem; else systemAvatar">
                  <vs-user-avatar
                    *ngIf="user(entry)?.photoURL as photoUrl; else initialsAvatar"
                    class="w-9 h-9 border border-base-white shadow-sm"
                    [config]="{ photoUrl: photoUrl }"
                  ></vs-user-avatar>

                  <ng-template #initialsAvatar>
                    <div
                      class="flex items-center justify-center w-9 h-9 rounded-full bg-primary-50 border border-base-white shadow-sm"
                    >
                      <span class="text-xs font-semibold text-primary-700">{{ initials(entry) }}</span>
                    </div>
                  </ng-template>
                </ng-container>

                <ng-template #systemAvatar>
                  <div
                    class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 border border-base-white shadow-sm"
                  >
                    <vs-icon name="cpu-chip-01" class="text-gray-500"></vs-icon>
                  </div>
                </ng-template>

                <div
                  class="absolute -right-1 -bottom-1 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-themed-500 border-2 border-base-white"
                  [theme]="entry.tone"
                >
                  <vs-icon size="10" class="text-base-white" [name]="$any(entry.category.icon)"></vs-icon>
                </div>
              </div>

              <div *ngIf="!last" class="w-px flex-1 bg-gray-200 my-1"></div>
            </div>

            <!-- What happened -->
            <div
              class="flex flex-col gap-3 flex-1 min-w-0 mb-4 px-4 py-3 rounded-lg border border-l-4 border-l-themed-400 bg-base-white shadow-sm"
              [theme]="entry.tone"
            >
              <div class="flex gap-3 items-start justify-between">
                <p class="text-sm text-gray-700 break-words min-w-0">
                  <span class="font-semibold text-gray-900">{{ actorName(entry) }}</span>
                  {{ entry.verb }}
                  <ng-container *ngIf="entry.subject">
                    {{ entry.connector }}
                    <span class="font-medium text-gray-900">{{ entry.subject }}</span>
                  </ng-container>
                </p>

                <span
                  class="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap"
                  tooltipStyle="light"
                  [vsTooltip]="(entry.changedAt | date: 'EEEE, MMM d, y, h:mm:ss a') || ''"
                >
                  <vs-icon name="clock" size="12"></vs-icon>
                  {{ entry.changedAt | date: 'h:mm a' }}
                </span>
              </div>

              <div class="flex flex-wrap items-center gap-1.5 -mt-1">
                <span
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-themed-50 text-themed-700"
                  [theme]="entry.category.theme"
                >
                  <vs-icon size="12" [name]="$any(entry.category.icon)"></vs-icon>
                  {{ entry.category.label }}
                </span>

                <span
                  *ngIf="entry.mode === 'created' || entry.mode === 'removed'"
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  [class.bg-success-50]="entry.mode === 'created'"
                  [class.text-success-700]="entry.mode === 'created'"
                  [class.bg-danger-50]="entry.mode === 'removed'"
                  [class.text-danger-700]="entry.mode === 'removed'"
                >
                  {{ entry.mode === 'created' ? 'New record' : 'Removed' }}
                </span>

                <span
                  *ngIf="entry.job"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs text-gray-600 max-w-full"
                >
                  <vs-icon name="tool-02" size="12" class="text-gray-400 shrink-0"></vs-icon>
                  <span class="truncate">{{ entry.job }}</span>
                </span>
              </div>

              <!-- Fields: before → after, or the values of a new or removed record -->
              <div
                *ngIf="entry.changes.length"
                class="rounded-md bg-gray-50 border border-gray-100 divide-y divide-gray-100"
              >
                <div
                  *ngFor="let change of visibleChangesOf(entry)"
                  class="grid grid-cols-[minmax(90px,30%)_1fr] gap-3 px-3 py-2 text-xs"
                >
                  <span class="font-medium text-gray-500 break-words">{{ change.label }}</span>

                  <div class="flex flex-wrap items-center gap-1.5 min-w-0">
                    <!-- Old value, ↓, new value: reads the same at any width -->
                    <div *ngIf="entry.mode === 'changed'; else singleValue" class="flex flex-col gap-1 w-full min-w-0">
                      <ng-container
                        *ngTemplateOutlet="value; context: { $implicit: change.before, isBefore: true }"
                      ></ng-container>
                      <vs-icon name="arrow-down" size="12" class="text-gray-400"></vs-icon>
                      <ng-container *ngTemplateOutlet="value; context: { $implicit: change.after }"></ng-container>
                    </div>

                    <ng-template #singleValue>
                      <ng-container
                        *ngTemplateOutlet="
                          value;
                          context: { $implicit: change.after ?? change.before, isBefore: entry.mode === 'removed' }
                        "
                      ></ng-container>
                    </ng-template>
                  </div>
                </div>

                <button
                  *ngIf="entry.changes.length > visibleChanges"
                  type="button"
                  class="w-full px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-gray-100 text-left"
                  (click)="toggleChanges(entry)"
                >
                  {{ isExpanded(entry) ? 'Show less' : 'Show all ' + entry.changes.length + ' fields' }}
                </button>
              </div>

              <!-- Money at the time of the change -->
              <div *ngIf="entry.amounts.length" class="flex flex-wrap gap-2">
                <div
                  *ngFor="let amount of entry.amounts"
                  class="flex flex-col px-3 py-1.5 rounded-md border bg-base-white"
                >
                  <span class="text-[11px] text-gray-500">{{ amount.label }}</span>
                  <span class="text-sm font-semibold text-gray-900 tabular-nums">{{ amount.value }}</span>
                </div>
              </div>

              <!-- Charges a decision covered -->
              <div *ngIf="entry.decidedLines.length" class="flex flex-col gap-1">
                <p class="text-xs font-medium text-gray-500">Charges in this decision</p>
                <div
                  *ngFor="let line of entry.decidedLines"
                  class="flex justify-between gap-3 text-xs text-gray-700 px-3 py-1 rounded bg-gray-50"
                >
                  <span class="truncate">{{ line.description }}</span>
                  <span *ngIf="line.amount" class="font-medium tabular-nums whitespace-nowrap">{{ line.amount }}</span>
                </div>
              </div>

              <!-- Evidence files a hold change linked -->
              <div *ngIf="entry.files.length" class="flex flex-wrap gap-1.5">
                <span
                  *ngFor="let file of entry.files"
                  class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs text-gray-700 bg-base-white"
                >
                  <vs-icon name="file-attachment-04" size="12" class="text-gray-400"></vs-icon>
                  {{ file }}
                </span>
              </div>

              <!-- Why -->
              <div
                *ngIf="entry.reason"
                class="flex gap-2 px-3 py-2 rounded-md bg-gray-50 border-l-2 border-gray-300 text-xs text-gray-700"
              >
                <vs-icon name="message-text-square-01" size="14" class="text-gray-400 shrink-0 mt-px"></vs-icon>
                <span class="italic whitespace-pre-line break-words">{{ entry.reason }}</span>
              </div>

              <!-- References, e.g. the authorization number or reason code -->
              <div *ngIf="entry.references.length" class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                <span *ngFor="let reference of entry.references">
                  {{ reference.label }}: <span class="font-medium text-gray-700">{{ reference.value }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <vs-pagination [config]="state.pagination" (pagination)="onPageChange($event)"></vs-pagination>
      </div>
    </vs-card-content>
  </ng-container>
</vs-card>

<!-- One value: a status badge, a long text block, an empty marker, or plain text -->
<ng-template #value let-value let-isBefore="isBefore">
  <ng-container *ngIf="value; else emptyValue">
    <span
      *ngIf="value.theme; else textValue"
      class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium bg-themed-50 text-themed-700"
      [theme]="value.theme"
    >
      <vs-icon name="circle" size="6" type="solid" class="text-themed-500"></vs-icon>
      {{ value.text }}
    </span>

    <ng-template #textValue>
      <span
        class="min-w-0 break-words"
        [class.whitespace-pre-line]="value.isLong"
        [class.text-gray-500]="isBefore"
        [class.text-gray-900]="!isBefore"
        [class.font-medium]="!isBefore"
      >
        {{ value.text }}
      </span>
    </ng-template>
  </ng-container>

  <ng-template #emptyValue>
    <span class="italic text-gray-400">empty</span>
  </ng-template>
</ng-template>
